﻿import { defineComponent } from 'vue';

import calendar_details from "./calendar_details.vue";
import calendar_month from "./calendar_month.vue";

import tour_list from "../tour_list/tour_list.vue";
import filter_list from "../tour_list/filter_list.vue";

import { Product } from '../../../../services/product';
import { Hidden_Tours } from '../../../../services/hidden_tours';

import { product_search } from "../../../../objects/v1_3/product/search/product_search";
import { product_detail_seller_information_month } from '../../../../objects/v1_3/product/product/product_detail_seller_information_month';
import { product_detail } from "../../../../objects/v1_3/product/product/product_detail";
import { filtertag } from "../../../../objects/v1_3/web_engine/filtertag";
import { week_day } from "../../../../objects/front_end/calendar/week_day";

const Prod = new Product;
const Hidden = new Hidden_Tours;

export default defineComponent({
    name: "calendar",
    components: {
        calendar_month,
        calendar_details,
        tour_list,
        filter_list
    },
    emits: [
        "tourSelected", "onSearchChange", "datePicked"
    ],
    props: {
    },
    data() {
        return {
            selected_month: new product_detail_seller_information_month() as product_detail_seller_information_month,
            product_search: new product_search() as product_search,
            tours_selected: new product_search() as product_search,
            travel_date: "" as string,
            months: [] as product_detail_seller_information_month[],
            isTourListOpen: false as boolean,
            search_results: "" as string,
            range_from: new week_day() as week_day,
            range_to: new week_day() as week_day,
            range_applied: false as boolean,
            // month to show once the months have loaded (Sync_Selection before Download_Product_Search finished)
            pending_month: null as { year: number; month: number } | null
        }
    },
    methods: {
        async onMonthChange(month: product_detail_seller_information_month) {
            this.selected_month = month;
            await this.$refs.calendar_details.onCalendarUpdate(this.$store.getters.get_Search_Filter_Tags, month);
        },
        Date_Selected(products_select: product_search, travel_date: string) {
            this.tours_selected = products_select;
            this.travel_date = travel_date;
            this.isTourListOpen = true;

            // single-date mode: let the page keep its own date filter in sync (travel_date is yyyy/m/d)
            const parts = (travel_date ?? "").split("/");
            if (parts.length == 3) {
                this.$emit("datePicked", parts[0] + "-" + parts[1].padStart(2, "0") + "-" + parts[2].padStart(2, "0"));
            }
        },
        // show the given dates (yyyy-mm-dd, to optional) as the selection, e.g. the side filter panel's travel dates when
        // the calendar is opened from the filter row; open on the start date's month
        Sync_Selection(output_from: string, output_to: string) {
            if (!output_from) {
                this.$refs.calendar_details?.Set_Range(new week_day(), new week_day());
                this.range_applied = false;
                return;
            }

            const day_from = new week_day();
            day_from.output_date = output_from;
            const day_to = new week_day();
            if (output_to && output_to != output_from) {
                day_to.output_date = output_to;
            }

            this.$refs.calendar_details?.Set_Range(day_from, day_to);
            this.range_applied = true;

            const [year, month] = output_from.split("-").map(Number);
            const found = this.months.find(m => m.year == year && m.month == month);
            if (found) {
                if (found.year != this.selected_month?.year || found.month != this.selected_month?.month) {
                    this.onMonthChange(found);
                }
            } else {
                this.pending_month = { year, month };
            }
        },
        Tour_Selected(travel_date: string, tour_id: string)
        {
            this.$emit("tourSelected", travel_date, tour_id);
        },
        async Download_Product_Search() {
            const product_search = await Prod.Get_Product_Search();
            await Hidden.Load();
            product_search.products = Hidden.Without_Hidden(product_search.products);
            this.months = Prod.Get_Product_Search_Months(product_search);

            await this.Calendar_Update();
        },
        async Calendar_Update() {
            if (this.months?.length > 0) {
                const pending = this.pending_month;
                this.pending_month = null;
                const start = (pending && this.months.find(m => m.year == pending.year && m.month == pending.month)) || this.months[0];

                this.onMonthChange(start);
                await this.onFilterChanges();
            }
        },
        async onFilterChanges() {
            await this.$refs.calendar_details.onCalendarUpdate(this.$store.getters.get_Search_Filter_Tags, this.selected_month);
        },
        Clear_Filter() {
            this.$refs.filter_list.Clear_Filter();
        },
        isRange(): boolean {
            return import.meta.env.VITE_DEFAULT_SEARCH_DATE_RANGE != "false";
        },
        onRangeChange(day_from: week_day, day_to: week_day) {
            this.range_from = day_from;
            this.range_to = day_to;
        },
        format_day(output_date: string | null): string {
            if ((output_date ?? "") == "") {
                return "";
            }

            // output_date is yyyy-mm-dd; build the date locally so it isn't shifted by the UTC parse of new Date("yyyy-mm-dd")
            const parts = String(output_date).split("-").map(Number);
            return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        },
        range_label(): string {
            const from = this.format_day(this.range_from.output_date);
            const to = this.format_day(this.range_to.output_date);

            if (from == "") {
                return "";
            }
            if (to == "" || to == from) {
                return from;
            }
            return from + " – " + to;
        },
        Apply_Chanages() {
            if ((this.range_from.output_date ?? "") == "") {
                return;
            }

            // a single selected day searches just that day
            const day_to = (this.range_to.output_date ?? "") == "" ? this.range_from : this.range_to;

            this.range_applied = true;
            this.$emit("onSearchChange", this.range_from, day_to, this.range_label());
        },
        // previous/next day buttons next to the Dates button: move the applied single day and search again
        Shift_Applied_Day(days: number) {
            const output_date = this.shift_date(this.range_from.output_date, days);
            if (output_date == "") {
                return;
            }

            const day = new week_day();
            day.output_date = output_date;

            this.$refs.calendar_details.Set_Range(day, new week_day());
            this.Apply_Chanages();
        },
        shift_date(output_date: string | null, days: number): string {
            if ((output_date ?? "") == "") {
                return "";
            }

            const parts = String(output_date).split("-").map(Number);
            const date = new Date(parts[0], parts[1] - 1, parts[2] + days);
            return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
        },
        Clear_Range() {
            this.$refs.calendar_details.Clear_Range();

            if (this.range_applied) {
                this.range_applied = false;
                this.$emit("onSearchChange", new week_day(), new week_day(), "");
            }
        },
        search_result(): string {
            let Search_Result: string = "";

            this.$store.getters.get_Search_Filter_Tags.forEach((tag: any) => {
                Search_Result += tag.name + " ";
            });

            return Search_Result;
        }
    },
    mounted() {
        this.Download_Product_Search();
    }
})